import React from 'react';
import type { Types } from '@ohif/core';
import { id } from './id.js';
import './brand.css';
import { Icons } from '@ohif/ui-next';
import {
  LaunchInfo,
  LaunchArrow,
  CheckBoxChecked,
  CheckBoxUnchecked,
  Magnifier,
  SycaiLogo,
} from './components/BrandIcons';
import { StudyListBrand } from './components/StudyListBrand';
import getCommandsModule from './getCommandsModule';
import getCustomizationModule from './getCustomizationModule';
import getToolbarModule from './getToolbarModule';
import getViewportModule from './getViewportModule';
import { SycaiService } from './services/SycaiService';
import { AccountRoleEnum, SessionService } from './services/SessionService';
import { ReportAssessmentService } from './services/ReportAssessmentService';
import { DiagnosisService } from './services/DiagnosisService';
import { OrganReviewService } from './services/OrganReviewService';
import { SeriesLabelService } from './services/SeriesLabelService';
import { setViewerUnauthorizedHandler, UnauthorizedException } from './http';
import { describeError, fatal, readCookie, rerootBulkDataUri } from './utils';
import { initRoots } from './config/roots';
import {
  DiagnosisCompleteVariant,
  showDiagnosisCompleteDialog,
} from './components/dialogs/DiagnosisCompleteDialog';

/**
 * Reads cookie `pacsUrl` and, ONLY if set, derives the three DICOMweb roots and
 * writes them into the active data source (§5.4).
 *
 * On the absent-cookie path the returned `sourceName` may be `undefined`: nothing
 * is dereferenced off `extensionManager`, deliberately, because that path is taken
 * by every local boot AND by M1's registration.test.ts, which calls
 * preRegistration with no extensionManager at all.
 *
 * One shot is sufficient, and that is a product fact rather than an optimisation
 * (D9): `pacsUrl` does not change within a session — only `accessToken` does. So
 * parent §4's second layer (`onConfiguration`, re-reading per viewer navigation)
 * is not merely unobservable in local dev, it is unnecessary; declining it also
 * declines its documented trap, where an onConfiguration that returns a fresh
 * object without spreading ...dicomWebConfig deletes the hook and silently
 * freezes the roots. Should the product fact change, the re-resolution path is
 * this same call plus its broadcast, which DataSourceWrapper.tsx:111-126 already
 * refreshes off.
 */
function applyPacsRootFromCookie(
  extensionManager?: Types.Extensions.ExtensionParams['extensionManager']
) {
  const pacsUrl = readCookie('pacsUrl');

  // ABSENT -> untouched, so sycai-local.js's relative roots stand. This is the
  // local dev path and M0's loop is unchanged.
  if (pacsUrl === null) {
    return { applied: false, sourceName: extensionManager?.activeDataSourceName };
  }

  // PRESENT BUT EMPTY is a deployment defect, not a fall-back (§5.4 point 3).
  // develop returned '' here, so the roots became relative /wado and /dicom-web
  // -- silently WORKING on the local proxy and silently BREAKING in deployment.
  const root = pacsUrl.trim().replace(/\/+$/, '');
  if (!root) {
    fatal('the pacsUrl cookie is present but empty; refusing to fall back to relative roots');
  }

  // The absent-cookie path above returns without touching extensionManager, which is what
  // lets registration.test.ts call preRegistration without one. Past that point every
  // line below dereferences it, so the precondition is asserted rather than assumed —
  // fatal() returns `never`, which is also what narrows the four reads that follow.
  if (!extensionManager) {
    fatal('the pacsUrl cookie is set but preRegistration supplied no extensionManager');
  }

  // §5.4 point 1: ExtensionManager.ts:557-560 makes an unknown source name a
  // silent no-op. develop hardcoded dataSources[0]; sycai-local.js names its
  // source `orthancProxy`. Resolve the ACTIVE name and assert the lookup hits.
  //
  // §5.4 point 2: this doubles as the assertion that @ohif/extension-default has
  // already registered its data sources. M1 deliberately APPENDED the
  // pluginConfig entry so that it has -- an assumption to assert, not rely on.
  const sourceName = extensionManager.activeDataSourceName;
  const dataSource = extensionManager.getActiveDataSource()?.[0];

  if (!dataSource) {
    fatal(
      `active data source "${sourceName}" is not registered at preRegistration time. ` +
        `Either the name is wrong in the app config, or @ohif/extension-sycai was ` +
        `moved ahead of @ohif/extension-default in pluginConfig.json.`
    );
  }

  // NOT dataSource.getConfig(). DicomWebDataSource's getConfig returns
  // `dicomWebConfigCopy` (extensions/default/src/DicomWebDataSource/index.ts:779-781),
  // and that variable is assigned only inside initialize() (:208) -- which first runs
  // on route entry, long after preRegistration. So getConfig() is `undefined` here,
  // and spreading it drops friendlyName, name, qidoSupportsIncludeField,
  // imageRendering, thumbnailRendering, dicomUploadEnabled and
  // omitQuotationForMultipartRequest, reducing the configuration to the three roots.
  // Observed doing exactly that while running check 3. The DEFINITION holds the live
  // configuration object -- ExtensionManager.ts:564 is what writes ours back into it --
  // so read it from there instead.
  const configuration = extensionManager.getDataSourceDefinition(sourceName)?.configuration;

  if (!configuration) {
    fatal(
      `active data source "${sourceName}" has no configuration to extend; refusing to ` +
        `replace it with the three roots alone.`
    );
  }

  // updateDataSourceConfiguration REPLACES the data-source instance
  // (ExtensionManager.ts:565), so nothing may hold a reference across this call
  // -- `dataSource` above is dead from here on. setActiveDataSource(sameName)
  // early-returns (:122-125) and is not a substitute.
  extensionManager.updateDataSourceConfiguration(sourceName, {
    ...configuration,
    wadoUriRoot: `${root}/wado`,
    wadoRoot: `${root}/dicom-web`,
    qidoRoot: `${root}/dicom-web`,

    // D3. Written HERE, from the same `root` the three lines above use, because the PACS
    // host is not knowable at config-authoring time -- a static startsWith/prefixWith in a
    // deploy config could only ever be right for one deployment. This preserves the
    // one-place-writes-the-roots invariant config/roots.ts:98-110 documents.
    //
    // `enabled: true` is MANDATORY, not decorative: DicomWebDataSource/index.ts:190-193
    // returns the naturalized dataset with NO resolver bound when bulkDataURI is present
    // but not enabled, so supplying the key without it would break every bulk-data consumer
    // in the app rather than only this one. :149's `||=` default is what we are replacing.
    //
    // `startsWith`/`prefixWith` set to the SAME `root` as `transform`, deliberately not a
    // no-op pair: fixBulkDataURI.ts:23-26 assigns `transform`'s result to a LOCAL
    // (`BulkDataURI = uriConfig.transform?.(BulkDataURI) || BulkDataURI`), and :30-33's
    // `startsWith` branch is the ONLY place in that function that writes a transformed
    // value back onto `value.BulkDataURI`, which is what retrieveBulkData actually reads.
    // Without this pair, `rerootBulkDataUri`'s result is always local and never persisted:
    // - Absolute root (e.g. `https://pacs.example.com`): the local starts with `http`, so
    //   :35's relative-resolution branch is skipped, and its first character is not `/`,
    //   so :60's branch is skipped too -- `value.BulkDataURI` falls through untouched,
    //   silently discarding the re-root and leaving the ORIGINAL cross-origin
    //   `http://127.0.0.1/...` URI in place.
    // - Relative root (e.g. `/pacs`): `value.BulkDataURI` still holds that same untouched
    //   original, which does not start with `/`, so :35 DOES fire and reruns the
    //   study/series relative-resolution logic against the local's already-absolute
    //   `/pacs/...` value, concatenating `wadoRoot` in front of it a second time --
    //   corrupting every bulk-data reference in the app, not only this one.
    // Because `root` matches what `transform` itself prepends, the write-back at :32
    // reconstructs the identical string: it strips `root` off the front and puts the same
    // `root` back on, so `value.BulkDataURI` ends up exactly equal to the transformed
    // local. That is what makes both shapes above resolve correctly instead.
    bulkDataURI: {
      enabled: true,
      transform: (bulkDataUri: string) => rerootBulkDataUri(bulkDataUri, root),
      startsWith: root,
      prefixWith: root,
    },
  });

  return { applied: true, sourceName };
}

/**
 * M5.4 D6. The study list's preview panel defaults open and takes roughly a quarter of the
 * page; develop has no such panel, and with it open no amount of correct colour makes the
 * two screens read as the same product (reference 01 against 09).
 *
 * useSessionStorage (platform/ui-next) reads sessionStorage synchronously in WorkList's
 * render body, so seeding here -- preRegistration is awaited during app boot, before any
 * route renders -- changes the DEFAULT without touching an upstream file.
 *
 * Only when unset. The hook writes the current value back on mount (useSessionStorage.tsx:
 * 64-66), so after the first visit the key always exists; re-seeding unconditionally would
 * overwrite the user's own choice on every reload. Written this way the user can still open
 * the panel and it stays open for the tab.
 *
 * Recorded as the milestone's one hack (D6): a private upstream key with no type contract,
 * no customization wrapping it, no export and no promise. Its failure mode is benign -- if
 * upstream renames it the panel defaults open again and the page still works.
 */
const STUDY_LIST_PREVIEW_KEY = 'studyList.previewOpen';

function seedStudyListPreviewClosed(): void {
  if (window.sessionStorage.getItem(STUDY_LIST_PREVIEW_KEY) === null) {
    window.sessionStorage.setItem(STUDY_LIST_PREVIEW_KEY, JSON.stringify({ open: false }));
  }
}

/**
 * Module scope, drained in onModeExit — the pattern at
 * extensions/cornerstone/src/index.tsx:108, :118-129, :171-175, and module scope
 * specifically because onModeExit receives only { servicesManager, commandsManager }
 * while onModeEnter receives four keys. Nothing captured on entry is handed back on exit.
 *
 * `unsubscriptions.length = 0` rather than reassignment: the binding is const, and
 * cornerstone drains it the same way.
 */
const unsubscriptions: Array<() => void> = [];

/**
 * The fork's extension. M1 shipped no product behaviour — only the registration
 * mechanisms every later milestone rides on.
 *
 * Registered globally (no `default: false` in pluginConfig.json), because product code
 * must run on the worklist too, so mode-scoped lazy loading would be wrong. Entries
 * without `default: false` are pushed into `extensions[]` by writePluginImportsFile.js
 * and registered at appInit.js:93,123 before any mode route. Precedent in tree:
 * @ohif/extension-ultrasound-pleura-bline.
 *
 * `onModeEnter` / `onModeExit` exist only to raise the completion dialogs (D7) — see the
 * comment on `onModeEnter` below for why that is safe despite the constraint that ruled
 * the hook out through M2: an extension's onModeEnter runs at
 * ExtensionManager.ts:168-179, BEFORE Mode.tsx:259-274 seeds Mode scope and long before the
 * mode's own onModeEnter -- reached through Mode.tsx:315's `mode?.onModeEnter(...)` call --
 * registers the toolbar (`registerModeToolbar`, called from `modes/basic/src/index.tsx:205`).
 * So it can read Default and Global but never the mode's toolbarButtons, its
 * modeCustomizations block, or any `mode`-phase config. It is where mechanism C's fallback
 * (a) would go if the customization route fails — see §7.1.
 */
const sycaiExtension = {
  /** Only required property. Unique across all extensions. */
  id,

  async preRegistration({
    servicesManager,
    extensionManager,
    appConfig,
  }: Types.Extensions.ExtensionParams) {
    // D-1: only 5 of 7 upstream icons differ from develop (IconColorLUT/OHIFLogoColorDarkBackground
    // unchanged). D-7: Icons.tsx's kebab aliases close over the stock imports, so both forms are registered.
    Icons.addIcon('LaunchInfo', LaunchInfo);
    Icons.addIcon('launch-info', LaunchInfo);
    Icons.addIcon('LaunchArrow', LaunchArrow);
    Icons.addIcon('CheckBoxChecked', CheckBoxChecked);
    Icons.addIcon('checkbox-checked', CheckBoxChecked);
    Icons.addIcon('checkbox-active', CheckBoxChecked);
    Icons.addIcon('CheckBoxUnchecked', CheckBoxUnchecked);
    Icons.addIcon('checkbox-unchecked', CheckBoxUnchecked);
    Icons.addIcon('checkbox-default', CheckBoxUnchecked);
    Icons.addIcon('Magnifier', Magnifier);
    Icons.addIcon('magnifier', Magnifier);

    // D8: appConfig is passed by reference; Header.tsx, WorkList.tsx, and the fork-owned
    // exception ViewerHeader.tsx (parent §5.2's four whiteLabeling call sites) read it optionally.
    // Unguarded, like the disableConfirmationPrompts write below: ExtensionParams.appConfig is
    // non-optional and always passed to preRegistration; registration.test.ts stubs it instead
    // of tolerating its absence.
    seedStudyListPreviewClosed();

    const whiteLabeling = (appConfig.whiteLabeling ??= {});
    // D14: M5.1 shipped OHIF's own icon as the fork's logo. develop branded OHIFLogo.tsx,
    // not OHIFLogoColorDarkBackground, which still carries fill="#5ACCE6".
    // No className: the artwork is a raster tuned for its native 100x28 (M5.2 plan D-2).
    // M5.4 D7: now the logo AND the study list's page title, which StudyListBrand gates on
    // the route so the viewer header keeps the bare logo (plan D-12).
    whiteLabeling.createLogoComponentFn = () => <StudyListBrand />;

    servicesManager.registerService(SycaiService.REGISTRATION);

    const { sycaiService } = servicesManager.services;
    console.info(sycaiService.getBanner());

    initRoots(extensionManager);

    // Before auth, deliberately: a session failure must not be able to stop the
    // study list from loading (§7.1 check 7).
    applyPacsRootFromCookie(extensionManager);

    servicesManager.registerService(SessionService.REGISTRATION);

    // BEFORE the await, deliberately: registration must be synchronous with respect to
    // preRegistration's caller so that a session failure cannot leave the product
    // services unregistered, and so that M1's registration.test.ts — which does not
    // await preRegistration — can observe them.
    //
    // ORDER IS LOAD-BEARING. DiagnosisService's constructor reads
    // servicesManager.services.reportAssessmentService synchronously, so this line must
    // precede the next one. A reversed or duplicated registration is a silent no-op
    // (ServicesManager.ts:39-44 warns and early-returns), which is why §7.1 check 1
    // asserts the order rather than assuming it. develop depended on the same ordering
    // and got it from appInit.js:88-89.
    servicesManager.registerService(ReportAssessmentService.REGISTRATION);
    servicesManager.registerService(DiagnosisService.REGISTRATION);
    servicesManager.registerService(OrganReviewService.REGISTRATION);

    // Order-free, unlike the ReportAssessment -> Diagnosis pair above: this constructor
    // stores servicesManager and reads nothing off it (plan D-12), so no peer needs to exist
    // yet. Registered here rather than later only so all six sit together.
    servicesManager.registerService(SeriesLabelService.REGISTRATION);

    const { sessionService, userAuthenticationService } = servicesManager.services;

    // Exactly two keys. UserAuthenticationProvider re-sets the other five in its
    // RENDER BODY on every render (UserAuthenticationProvider.tsx:94-102), so
    // supplying them here would be overwritten silently. Merging is by
    // truthiness, not presence (UserAuthenticationService.ts:68-73) —
    // implementations can only be added, never cleared.
    userAuthenticationService.setServiceImplementation({
      getAuthorizationHeader: () => {
        // Read PER CALL (D3, D9): the accessToken cookie changes mid-session and
        // there is no refresh hook, so this is the only mechanism that keeps up.
        const accessToken = readCookie('accessToken');
        return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
      },
      handleUnauthenticated: () => {
        sessionService.logOut();
      },
    });

    /**
     * D6. Order relative to setServiceImplementation is free, unlike the registration
     * pair above: what is registered here is a closure, and
     * UserAuthenticationService.ts:47's `handleUnauthenticated()` re-reads
     * `this.serviceImplementation` on every call, so the lookup happens when a 401 lands
     * and finds the implementation installed above however these two lines are ordered.
     * Adjacency is style.
     */
    setViewerUnauthorizedHandler(() => userAuthenticationService.handleUnauthenticated());

    // The try wraps the AWAIT AND NOTHING ELSE. When the role
    // gate lived inside it, a TypeError thrown BY THE GATE -- `account` not an
    // object, or no `appConfig` -- was caught below, failed the
    // `instanceof UnauthorizedException` test and printed
    // "session verification failed", which is a lie: verification had SUCCEEDED.
    // The gate would then silently not apply, leaving disableConfirmationPrompts
    // at whatever the config set. Under sycai-local.js:69's hardcoded `true` that
    // is REVIEWER prompt-suppression granted to a non-REVIEWER -- exactly the
    // state §7.2 check 3 exists to catch, hidden behind a misleading log line.
    let account;

    try {
      account = await sessionService.verifySession();
    } catch (error) {
      if (error instanceof UnauthorizedException) {
        userAuthenticationService.handleUnauthenticated();
        return;
      }

      // Plan D-3: only a 401 logs out. develop caught EVERYTHING here and
      // redirected, which would make every local boot unobservable -- and with no
      // refresh endpoint (§5.5) a logout triggered by a timeout is unrecoverable.
      //
      // describeError() decides how much of an error is safe to print, and
      // DiagnosisService.report shares it: neither of its branches can reach
      // the account object, a token or a cookie (§6.1).
      console.error(
        `[sycai] session verification failed ${describeError(error)}; ` +
          `continuing without a session`
      );
      return;
    }

    // ASSIGNMENT, not a conditional set: a non-REVIEWER must have the flag
    // CLEARED even where a config hardcoded it, which is what §7.2 check 3
    // asserts. promptHydrationDialog.ts reads extensionManager.appConfig inside
    // the function (:72-76) and gates RT and SEG on
    // !appConfig?.disableConfirmationPrompts (:82); SR is governed separately
    // by measurementTrackingMode. Runtime mutation of this flag is a sanctioned
    // upstream pattern (modes/usAnnotation/src/index.ts:97,270).
    //
    // Unguarded on purpose. verifySession() now returns only a validated Account
    // (SessionService's isAccount), and appConfig is always passed to
    // preRegistration (ExtensionManager.ts:283), so a throw here is essentially
    // unreachable.
    //
    // A throw here does NOT reach fatal()'s channel -- it is not wired to it at
    // all. This assignment sits OUTSIDE the try/catch above -- deliberately,
    // since a throw from the role gate must not be caught below and misreported
    // as a failed session verification -- so a throw propagates out of
    // preRegistration uncaught by us, but preRegistration is itself invoked from
    // inside ExtensionManager's own `try { await extension.preRegistration(...)
    // } catch (error) { console.error(error) }`
    // (ExtensionManager.ts:239-241,277; utils/fatal.ts:1-13 documents this
    // swallow correctly). Because module registration (getToolbarModule,
    // getCustomizationModule) happens at ExtensionManager.ts:296+, AFTER that
    // awaited call, a throw here would boot this extension half-registered --
    // toolbar and customization modules silently absent -- with nothing but a
    // console line to show for it. Leaving the assignment unguarded is still the
    // right call: not because the failure is safe, but because it is that
    // unreachable.
    appConfig.disableConfirmationPrompts = account.role === AccountRoleEnum.REVIEWER;
  },

  /**
   * D7. The completion dialogs are raised from here rather than from a component,
   * because all four UI providers mount ABOVE BrowserRouter (App.tsx:114-128 wrapping
   * :172-174) — so a dialog raised from anywhere survives route re-render and is never
   * unmounted by ModeRoute. In-tree precedent: extensions/cornerstone/src/init.tsx:420-423
   * raises uiModalService.show() from extension init code in reaction to an async event.
   *
   * Safe because §5.4's teardown sweep found exactly two services that destroy their
   * subscribers on a mode hook — customizationService (enter AND exit) and
   * segmentationService (exit) — and diagnosisService is neither. Note the failure mode
   * when that is got wrong: after super.reset() the unsubscribe closures still returned
   * by subscribe become no-ops without throwing (pubSubServiceInterface.ts:51-53). Silent.
   *
   * NOT during preRegistration. M1 §8 and parent §4 warned about exactly this milestone.
   */
  onModeEnter({ servicesManager }: { servicesManager: AppTypes.ServicesManager }) {
    const { diagnosisService, uiModalService } = servicesManager.services;

    // uiModalService is declared optional by platform/core's AppTypes.Services, which the
    // fork does not modify — so unlike the fork's own four (types/AppTypes.ts) it cannot
    // be made non-optional at the declaration. appInit.js registers it before any mode
    // route, so by onModeEnter it is always present; the assertion says so at the one
    // place that depends on it, rather than at each of the two below.
    const modalService = uiModalService as AppTypes.UIModalService;

    const raise = (variant: DiagnosisCompleteVariant) =>
      ({ studyInstanceUid }: { studyInstanceUid: string }) => {
        // §5.7. develop guarded with `studyInstanceUIDs?.includes(studyInstanceUid)`,
        // which an extension's onModeEnter cannot do: it receives no study UIDs, no
        // route params, and ExtensionManager.onModeEnter() takes no parameters at all —
        // the object Mode.tsx:235-240 passes it is silently discarded. Asking the
        // service which study it is tracking needs neither ImageViewerProvider nor the
        // hanging protocol.
        if (studyInstanceUid !== diagnosisService.getTrackedStudy()) {
          return;
        }

        showDiagnosisCompleteDialog(modalService, variant);
      };

    unsubscriptions.push(
      diagnosisService.subscribe(DiagnosisService.EVENTS.DIAGNOSIS_COMPLETED, raise('completed'))
        .unsubscribe,
      diagnosisService.subscribe(DiagnosisService.EVENTS.DIAGNOSIS_FAILED, raise('failed'))
        .unsubscribe
    );
  },

  /**
   * Unconditional, and stricter than develop's. The cleanup in DEVELOP's
   * platform/app/src/routes/Mode/Mode.tsx (:225-232 there; that range is unrelated code
   * on this branch) early-returns when studyInstanceUIDs is empty — which is the first
   * run, since the effect guards only on ExtensionDependenciesLoaded while
   * studyInstanceUIDs starts null — and then re-runs when the UIDs populate, registering
   * a second pair of listeners. It also only ever passed studyInstanceUIDs[0]. This
   * takes neither hazard.
   */
  onModeExit() {
    unsubscriptions.forEach(unsubscribe => unsubscribe());
    unsubscriptions.length = 0;
  },

  getToolbarModule,
  getCustomizationModule,
  getCommandsModule,
  getViewportModule,
};

export { default as InvestigationalUseDialog } from './components/InvestigationalUseDialog';

export default sycaiExtension;
